/**
 * mavin-recommender.js  -  MavinWear Size Chart + Size Recommendation
 * --------------------------------------------------------------------
 * One branded popup with two tabs:
 *   1) Size Chart            - the product's garment measurements (cm)
 *   2) Size Recommendation   - enter height + weight, get the size instantly
 *
 * Two links ("Size chart" / "Size recommendation") are injected next to the
 * product's size selector. The recommender math is calibrated against
 * MavinWear's own height/weight matrix (100% match on the reference tee).
 *
 * Data + algorithm are client-side (no backend). ASCII-only (Shopify rule).
 */
(function () {
  'use strict';

  // ---- Garment data (flat half-chest width + total length, cm) ------------
  var CHARTS = {
    'compression':   { name: 'Compression Tee',  fit: 'compression', type: 'top',
                       sizes: ['S','M','L','XL'], length: [60,63,67,67.5], chest: [39,42.5,45,49.5] },
    'slim-tee':      { name: 'Rival Slim Tee',    fit: 'slim',        type: 'top',
                       sizes: ['S','M','L','XL'], length: [70,71,73,76], chest: [50.5,52,53,58] },
    'oversized-tee': { name: 'Rival Oversized Tee', fit: 'oversized', type: 'top',
                       sizes: ['S','M','L','XL'], length: [69,71,73,76], chest: [59,61,63,68] },
    'oversized-tank':{ name: 'Rival Oversized Tank', fit: 'oversized', type: 'top',
                       sizes: ['S','M','L','XL'], length: [69,71,73,77], chest: [59,60,63,67] },
    'tank-rib':      { name: 'Rib / Contrast Tank', fit: 'compression', type: 'top',
                       sizes: ['S','M','L','XL'], length: [58.5,60.5,62.5,64.5], chest: [36,39,42,47] },
    'contour':       { name: 'Contour Fit Tee',   fit: 'slim',        type: 'top',
                       sizes: ['S','M','L','XL'], length: [66,68,70,72], chest: [45,49,53,57] },
    'sweatpants':    { name: 'Sweatpants',        fit: 'regular',     type: 'bottom',
                       sizes: ['S','M','L','XL'], inseam: [74,76,78,82], totalLength: [105,107,109,112],
                       waist: [36,37,39,40], hips: [53,56,60,68] }
  };

  // ---- Per-product-type fallback charts -----------------------------------
  // Used ONLY for the recommendation when a product has no assigned size chart
  // (e.g. jerseys, shorts). Tuned so an average build (about 175 cm / 75 kg)
  // lands on M for that garment type. These never show a chart - recommend only.
  // Each averaged from ALL of that type's real size-chart pages (see
  // scripts/retune.py). Used only for the recommendation when a product has no
  // chart of its own; never displayed as a chart.
  var FALLBACK = {
    'tee':         { name: 'T-Shirt',   fit: 'oversized',   type: 'top',
                     sizes: ['S','M','L','XL'], length: [68.5,70.5,72.5,75], chest: [56.5,59.5,62.5,67] },
    'jersey':      { name: 'Jersey',    fit: 'oversized',   type: 'top',
                     sizes: ['S','M','L','XL'], length: [69.7,71.7,73.7,75.7], chest: [58.3,61.7,65,68.3] },
    'tank':        { name: 'Tank Top',  fit: 'compression', type: 'top',
                     sizes: ['S','M','L','XL'], length: [60,62,64,66], chest: [38,41,44,48] },
    'compression': { name: 'Compression', fit: 'compression', type: 'top',
                     sizes: ['S','M','L','XL'], length: [63.3,65.7,68.3,69.8], chest: [38.3,42.2,45.7,49.8] },
    'hoodie':      { name: 'Hoodie',    fit: 'regular',     type: 'top',
                     sizes: ['S','M','L','XL'], length: [66,68,70,72], chest: [56,60,64,68] },
    'jacket':      { name: 'Jacket',    fit: 'oversized',   type: 'top',
                     sizes: ['S','M','L','XL'], length: [66,68,70,72], chest: [60,62,64,66] },
    'shorts':      { name: 'Shorts',    fit: 'regular',     type: 'bottom',
                     sizes: ['S','M','L','XL'], inseam: [22,23,24,25], waist: [33,36,39,42], hips: [53,55,57,59] },
    'sweatpants':  { name: 'Sweatpants', fit: 'regular',    type: 'bottom',
                     sizes: ['S','M','L','XL'], inseam: [74,76,78,82], waist: [36,37,39,40], hips: [53,56,60,68] }
  };

  // Per-type ease (cm), re-tuned from 3,000+ real exchanges (scripts/retune.py).
  // + ease = more room (style runs small -> recommend bigger for borderline
  // builds); - ease = less room (runs large). Clamped so the typical build's
  // size never flips - only borderline shoppers move, in the data's direction.
  var EASE_BY_TYPE = {
    'compression': -5.2, 'slim-tee': 14.1, 'contour': 4.4,
    'oversized-tee': 14.1, 'oversized-tank': 13, 'tank-rib': -13.6,
    'tee': 14.1, 'jersey': 16.4, 'shorts': -0.9, 'sweatpants': -4
  };

  // Style fit offset applied to the final size: +1 = runs small (size up),
  // -1 = runs large (size down). Set per page from the theme setting / product
  // metafield (Customize > Theme settings, or product "Size fit" field).
  var FIT_ADJUST = 0;

  // Ease (cm) added to body chest, per garment fit class. Calibrated so the
  // slim tee reproduces the owner's height/weight matrix exactly.
  var EASE = { compression: -8, slim: 12, regular: 12, oversized: 18 };

  // ---- Inline icons (currentColor) ----------------------------------------
  var RULER_SVG = '<svg class="mr-ico" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="2" y="7" width="20" height="10" rx="1"></rect><line x1="7" y1="7" x2="7" y2="11"></line><line x1="12" y1="7" x2="12" y2="12"></line><line x1="17" y1="7" x2="17" y2="11"></line></svg>';
  var CHECK_SVG = '<svg class="mr-ico" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6L9 17l-5-5"></path></svg>';

  // 2D garment diagram (illustrates the measured Length + Width to the shopper).
  function diagram(chart) {
    var O = '#e8732e';   // measurement-arrow accent (matches the reference diagram)
    if (chart.type === 'bottom') {
      return '<div class="mr-figure"><svg viewBox="0 0 200 200" width="150" height="150" role="img" aria-label="How we measure">' +
        '<path d="M58 18 H142 V58 L150 184 H120 L102 86 L98 86 L80 184 H50 L58 58 Z" fill="#fafafa" stroke="#111" stroke-width="3" stroke-linejoin="round"></path>' +
        '<line x1="60" y1="30" x2="140" y2="30" stroke="' + O + '" stroke-width="2"></line>' +
        '<polygon points="56,30 66,26 66,34" fill="' + O + '"></polygon><polygon points="144,30 134,26 134,34" fill="' + O + '"></polygon>' +
        '<text x="100" y="24" font-size="12" fill="' + O + '" font-weight="700" text-anchor="middle">Waist</text>' +
        '<line x1="92" y1="90" x2="92" y2="182" stroke="' + O + '" stroke-width="2"></line>' +
        '<polygon points="92,86 88,96 96,96" fill="' + O + '"></polygon><polygon points="92,186 88,176 96,176" fill="' + O + '"></polygon>' +
        '<text x="100" y="140" font-size="12" fill="' + O + '" font-weight="700">Inseam</text>' +
        '</svg></div>';
    }
    return '<div class="mr-figure"><svg viewBox="0 0 240 184" width="184" height="141" role="img" aria-label="How we measure">' +
      '<path d="M84 16 L50 30 L26 58 L50 80 L70 60 L70 164 L170 164 L170 60 L190 80 L214 58 L190 30 L156 16 C156 35 132 45 120 45 C108 45 84 35 84 16 Z" fill="#fafafa" stroke="#111" stroke-width="3" stroke-linejoin="round"></path>' +
      '<line x1="86" y1="56" x2="86" y2="162" stroke="' + O + '" stroke-width="2"></line>' +
      '<polygon points="86,52 82,62 90,62" fill="' + O + '"></polygon><polygon points="86,166 82,156 90,156" fill="' + O + '"></polygon>' +
      '<text x="94" y="115" font-size="12" fill="' + O + '" font-weight="700">Length</text>' +
      '<line x1="74" y1="76" x2="166" y2="76" stroke="' + O + '" stroke-width="2"></line>' +
      '<polygon points="70,76 80,72 80,80" fill="' + O + '"></polygon><polygon points="170,76 160,72 160,80" fill="' + O + '"></polygon>' +
      '<text x="120" y="70" font-size="12" fill="' + O + '" font-weight="700" text-anchor="middle">Chest width</text>' +
      '</svg></div>';
  }

  // ---- Core math ----------------------------------------------------------
  function bodyChest(H, W) { return 0.45 * W - 0.2 * (H - 170) + 56; }   // cm circumference
  function bodyWaist(H, W) { return 0.50 * W + 0.30 * (H - 170) + 28; }  // cm circumference (bottoms)
  function bodyHip(H, W)   { return 0.42 * W + 0.20 * (H - 170) + 50; }

  function heightIndex(H) { return H <= 171 ? 0 : H <= 177 ? 1 : H <= 184 ? 2 : 3; }

  function smallestGE(arr, need) {
    for (var i = 0; i < arr.length; i++) { if (arr[i] >= need) return i; }
    return arr.length - 1;                       // clamp to largest
  }

  // Returns { rec, neighbor, neighborNote, confidence } or null
  function recommend(chart, H, W) {
    var last = chart.sizes.length - 1, rec, ci, lowerBound, slack, band, pos, neighbor = -1, note = '';

    if (chart.type === 'top') {
      var ease = (chart.ease != null) ? chart.ease : (EASE[chart.fit] || 12);
      var reqW = (bodyChest(H, W) + ease) / 2;
      ci = smallestGE(chart.chest, reqW);
      var hi = heightIndex(H);
      // Weight floor: for heavy builds the chest formula under-predicts on oversized
      // garments (ease is large enough that S "fits" even at 100kg), causing weight
      // to disappear from the recommendation. This minimum ensures weight stays visible.
      // Thresholds: >100kg -> at least L; >87kg -> at least M.
      var wFloor = W >= 100 ? 2 : W > 85 ? 1 : 0;
      rec = Math.max(ci, hi, wFloor);
      lowerBound = rec > 0 ? chart.chest[rec - 1] : chart.chest[rec] - 4;
      slack = chart.chest[rec] - reqW;            // cm of room in the picked size
      band = chart.chest[rec] - lowerBound;
    } else {
      var beEase = (chart.ease != null) ? chart.ease : 0;
      var reqWaist = (bodyWaist(H, W) + beEase) / 2;   // flat half-waist
      var reqHip = (bodyHip(H, W) + beEase) / 2;
      var wi = smallestGE(chart.waist, reqWaist);
      var pi = smallestGE(chart.hips, reqHip);
      ci = Math.max(wi, pi);
      rec = ci;
      var ref = (pi >= wi) ? chart.hips : chart.waist;
      var req = (pi >= wi) ? reqHip : reqWaist;
      lowerBound = rec > 0 ? ref[rec - 1] : ref[rec] - 4;
      slack = ref[rec] - req;
      band = ref[rec] - lowerBound;
    }

    pos = Math.max(0, Math.min(1, band > 0 ? slack / band : 0.5)); // 1=roomy(near lower edge) 0=tight(near upper)

    if (pos < 0.22 && rec < last) { neighbor = rec + 1; note = 'if you prefer a looser fit'; }
    else if (pos > 0.74 && rec > 0 && ci >= heightIndex(H)) { neighbor = rec - 1; note = 'if you prefer a tighter, fitted look'; }
    // Suppress "size down" neighbor when the weight floor was the deciding factor —
    // offering a smaller size would undermine the weight-based minimum.
    if (neighbor > -1 && neighbor < rec && typeof wFloor !== 'undefined' && wFloor > Math.max(ci, heightIndex(H))) {
      neighbor = -1;
    }

    // Style runs small/large -> shift the whole recommendation by one size.
    var adjusted = false;
    if (FIT_ADJUST) {
      var shifted = Math.max(0, Math.min(last, rec + FIT_ADJUST));
      if (shifted !== rec) { rec = shifted; adjusted = true; }
      if (neighbor > -1) {
        neighbor = neighbor + FIT_ADJUST;
        if (neighbor < 0 || neighbor > last || neighbor === rec) neighbor = -1;
      }
    }

    var confidence = (pos >= 0.30 && pos <= 0.68) ? 'high' : 'medium';
    return { rec: rec, neighbor: neighbor, neighborNote: note, confidence: confidence, adjusted: adjusted };
  }

  // ---- Build the modal once -----------------------------------------------
  // showChart / showRec gate which tab(s) appear (theme toggles + fallback mode).
  function buildModal(chart, activeTab, showChart, showRec) {
    var both = showChart && showRec;

    var chartPanel = '';
    if (showChart) {
      var sizeRows = '';
      for (var i = 0; i < chart.sizes.length; i++) {
        var c2, c3;
        if (chart.type === 'top') { c2 = chart.chest[i]; c3 = chart.length[i]; }
        else { c2 = chart.waist[i]; c3 = chart.inseam[i]; }
        sizeRows += '<tr><td class="mr-sz">' + chart.sizes[i] + '</td><td>' + c2 + '</td><td>' + c3 + '</td></tr>';
      }
      var head2 = chart.type === 'top' ? 'Chest (cm)' : 'Waist (cm)';
      var head3 = chart.type === 'top' ? 'Length (cm)' : 'Inseam (cm)';
      chartPanel =
        '<div class="mr-panel" data-panel="chart">' +
          '<p class="mr-sub">' + chart.name + ' - measurements in cm</p>' +
          diagram(chart) +
          '<table class="mr-table"><thead><tr><th>Size</th><th>' + head2 + '</th><th>' + head3 + '</th></tr></thead>' +
          '<tbody>' + sizeRows + '</tbody></table>' +
          '<p class="mr-foot">Measured flat. Chest = pit-to-pit. If between sizes, size up for a looser fit.</p>' +
        '</div>';
    }

    var recPanel = '';
    if (showRec) {
      recPanel =
        '<div class="mr-panel" data-panel="rec">' +
          '<p class="mr-sub">FIND YOUR SIZE</p>' +
          '<div class="mr-fields">' +
            '<label class="mr-field"><span>Height</span><div class="mr-input"><input type="number" inputmode="numeric" id="mr-h" min="120" max="220" placeholder="175"><em>cm</em></div></label>' +
            '<label class="mr-field"><span>Weight</span><div class="mr-input"><input type="number" inputmode="numeric" id="mr-w" min="35" max="180" placeholder="75"><em>kg</em></div></label>' +
          '</div>' +
          '<button class="mr-go" type="button">Find my size</button>' +
          '<div class="mr-result" hidden></div>' +
        '</div>';
    }

    var tabs = both
      ? '<div class="mr-tabs">' +
          '<button class="mr-tab" data-tab="chart">SIZE CHART</button>' +
          '<button class="mr-tab" data-tab="rec">SIZE RECOMMENDATION</button>' +
        '</div>'
      : '';

    var html =
      '<div class="mr-overlay" role="dialog" aria-modal="true" aria-label="Size guide">' +
        '<div class="mr-modal">' +
          '<button class="mr-close" aria-label="Close">&times;</button>' +
          '<h2 class="mr-title">SIZE GUIDE</h2>' +
          tabs + chartPanel + recPanel +
        '</div>' +
      '</div>';

    var wrap = document.createElement('div');
    wrap.className = 'mr-root';
    wrap.innerHTML = html;
    document.body.appendChild(wrap);
    return wrap;
  }

  function renderResult(chart, resultEl) {
    var H = parseFloat(document.getElementById('mr-h').value);
    var W = parseFloat(document.getElementById('mr-w').value);
    if (!H || !W || H < 120 || H > 220 || W < 35 || W > 180) {
      resultEl.hidden = false;
      resultEl.innerHTML = '<p class="mr-err">Enter a valid height (120-220 cm) and weight (35-180 kg).</p>';
      return;
    }
    var r = recommend(chart, H, W);
    var recSize = chart.sizes[r.rec];
    var html = '<div class="mr-rec"><span class="mr-rec-label">We recommend</span>' +
               '<span class="mr-rec-size">' + recSize + '</span>' +
               '<span class="mr-rec-tag">Recommended</span></div>';
    if (r.neighbor > -1) {
      html += '<p class="mr-alt"><b>' + chart.sizes[r.neighbor] + '</b> ' + r.neighborNote + '</p>';
    } else {
      html += '<p class="mr-alt mr-alt--sure">This is a confident fit for your measurements.</p>';
    }
    if (r.adjusted) {
      html += '<p class="mr-note">Adjusted for this style' + (FIT_ADJUST > 0 ? ' (runs small)' : ' (runs large)') + '.</p>';
    }
    resultEl.hidden = false;
    resultEl.innerHTML = html;
  }

  // ---- Wire one product ---------------------------------------------------
  function init() {
    var mount = document.getElementById('mavin-recommender');
    if (!mount || mount.dataset.mrReady) return;
    // Prefer the merchant-editable metaobject data (Content > Metaobjects);
    // fall back to the built-in CHARTS copy if it is missing/unparseable.
    var key = mount.dataset.chart, fbKey = mount.dataset.fallback, DATA = {};
    var dataEl = document.getElementById('mavin-recommender-data');
    if (dataEl) { try { DATA = JSON.parse(dataEl.textContent); } catch (e) { DATA = {}; } }
    // Prefer the product's own chart; otherwise the per-type fallback (rec only).
    var chart = (key && (DATA[key] || CHARTS[key])) || (fbKey && (FALLBACK[fbKey] || CHARTS[fbKey]));
    if (!chart) return;
    mount.dataset.mrReady = '1';

    // Apply the exchange-tuned per-type ease (overrides the generic fit ease).
    var ekey = key || fbKey;
    if (EASE_BY_TYPE[ekey] != null) chart.ease = EASE_BY_TYPE[ekey];

    // Style runs small/large -> shift the recommendation by one size.
    FIT_ADJUST = mount.dataset.fit === 'small' ? 1 : mount.dataset.fit === 'large' ? -1 : 0;

    // Links row - respect the theme-editor on/off toggles. A fallback (no real
    // chart) forces the Size chart link off and shows recommendation only.
    var showChart = mount.dataset.showChart !== '0';
    var showRec = mount.dataset.showRec !== '0';
    if (!showChart && !showRec) return;
    var parts = [];
    if (showChart) parts.push('<button type="button" class="mr-link" data-open="chart">' + RULER_SVG + ' Size chart</button>');
    if (showRec) parts.push('<button type="button" class="mr-link" data-open="rec">' + CHECK_SVG + ' Size recommendation</button>');
    var row = document.createElement('div');
    row.className = 'mr-links';
    row.innerHTML = parts.join('<span class="mr-dot">&middot;</span>');

    placeLinks(row);

    var modal = null;
    function open(tab) {
      if (!showChart) tab = 'rec';
      else if (!showRec) tab = 'chart';
      if (!modal) { modal = buildModal(chart, tab, showChart, showRec); bindModal(modal, chart); }
      switchTab(modal, tab);
      modal.querySelector('.mr-overlay').classList.add('mr-open');
      document.documentElement.style.overflow = 'hidden';
    }
    row.querySelectorAll('.mr-link').forEach(function (b) {
      b.addEventListener('click', function () { open(b.dataset.open); });
    });
  }

  function bindModal(modal, chart) {
    var overlay = modal.querySelector('.mr-overlay');
    function close() { overlay.classList.remove('mr-open'); document.documentElement.style.overflow = ''; }
    modal.querySelector('.mr-close').addEventListener('click', close);
    overlay.addEventListener('click', function (e) { if (e.target === overlay) close(); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') close(); });
    modal.querySelectorAll('.mr-tab').forEach(function (t) {
      t.addEventListener('click', function () { switchTab(modal, t.dataset.tab); });
    });
    var resultEl = modal.querySelector('.mr-result');
    var goBtn = modal.querySelector('.mr-go');
    if (goBtn) {
      goBtn.addEventListener('click', function () { renderResult(chart, resultEl); });
      var live = function () {
        var H = parseFloat(document.getElementById('mr-h').value), W = parseFloat(document.getElementById('mr-w').value);
        if (H >= 120 && W >= 35) renderResult(chart, resultEl);
      };
      modal.querySelector('#mr-h').addEventListener('input', live);
      modal.querySelector('#mr-w').addEventListener('input', live);
    }
  }

  function switchTab(modal, tab) {
    modal.querySelectorAll('.mr-tab').forEach(function (t) { t.classList.toggle('mr-tab--on', t.dataset.tab === tab); });
    modal.querySelectorAll('.mr-panel').forEach(function (p) { p.classList.toggle('mr-panel--on', p.dataset.panel === tab); });
  }

  // Place the links where the theme's old "Size guide" link sits and KEEP them
  // there. The theme re-renders the variant picker each time the shopper changes
  // size, which drops our row and un-hides the native link, so we re-apply on
  // every DOM change (debounced to one run per animation frame).
  function placeLinks(row) {
    function anchor() {
      return document.querySelector('.product__size-guide') ||
             document.querySelector('.product__variant-picker') ||
             (function () {
               var btn = document.querySelector('product-form button[name="add"], button[name="add"]');
               return btn && (btn.closest('product-form') || btn.closest('form'));
             })();
    }
    function ensure() {
      // Hide every native "Size guide" link (a fresh one appears on re-render).
      var guides = document.querySelectorAll('.product__size-guide');
      for (var i = 0; i < guides.length; i++) {
        if (guides[i].style.display !== 'none') guides[i].style.display = 'none';
      }
      // Re-insert our row whenever it has been detached by a re-render.
      if (!row.isConnected) {
        var a = anchor();
        if (a && a.parentNode) a.parentNode.insertBefore(row, a);
      }
    }
    ensure();
    var scheduled = false;
    var obs = new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(function () { scheduled = false; ensure(); });
    });
    obs.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
